import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "../../App";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { ClipboardList, UserPlus, Trophy, Calendar } from "lucide-react";

const ContestantHomePage = () => {
  const [playdays, setPlaydays] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPlaydays();
  }, []);

  const fetchPlaydays = async () => {
    try {
      const response = await axios.get(`${API}/playdays`);
      setPlaydays(response.data.slice(0, 3));
    } catch (error) {
      console.error("Failed to fetch playdays");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="contestant-home">
      {/* Hero Card */}
      <Card className="bg-primary border-none overflow-hidden">
        <CardContent className="p-6 relative">
          <div className="relative z-10">
            <h1 className="font-heading font-bold text-2xl text-primary-foreground mb-2">
              Ready to Compete?
            </h1>
            <p className="text-primary-foreground/80 mb-4">
              Register for upcoming events, check your stats, and view live results.
            </p>
            <Link to="/contestant/register">
              <Button className="bg-white text-primary-foreground hover:bg-white/90 font-bold" data-testid="register-cta">
                <ClipboardList className="h-4 w-4 mr-2" />
                Register Now
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 gap-4">
        <Link to="/contestant/individual-membership">
          <Card className="border-primary/20 hover:border-primary transition-colors card-hover">
            <CardContent className="p-4 text-center">
              <div className="w-12 h-12 bg-primary/20 rounded-xl flex items-center justify-center mx-auto mb-3">
                <UserPlus className="h-6 w-6 text-primary" />
              </div>
              <h3 className="font-heading font-bold text-text-primary">Individual</h3>
              <p className="text-sm text-text-secondary">$15 Membership</p>
            </CardContent>
          </Card>
        </Link>
        <Link to="/contestant/family-membership">
          <Card className="border-primary/20 hover:border-primary transition-colors card-hover">
            <CardContent className="p-4 text-center">
              <div className="w-12 h-12 bg-accent/20 rounded-xl flex items-center justify-center mx-auto mb-3">
                <Trophy className="h-6 w-6 text-accent" />
              </div>
              <h3 className="font-heading font-bold text-text-primary">Family</h3>
              <p className="text-sm text-text-secondary">$50 Membership</p>
            </CardContent>
          </Card>
        </Link>
      </div>

      {/* Upcoming Events */}
      <div>
        <h2 className="font-heading font-bold text-xl text-text-primary mb-4">Upcoming Events</h2>
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-4 border-primary border-t-transparent mx-auto"></div>
          </div>
        ) : playdays.length === 0 ? (
          <Card className="border-primary/20">
            <CardContent className="p-6 text-center">
              <Calendar className="h-12 w-12 text-text-muted mx-auto mb-3" />
              <p className="text-text-secondary">No upcoming events scheduled</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {playdays.map((playday) => (
              <Card key={playday.id} className="border-primary/20">
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-secondary rounded-lg flex items-center justify-center">
                      <Calendar className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-heading font-bold text-text-primary">{playday.name}</h3>
                      <p className="text-sm text-text-secondary">{playday.date}</p>
                    </div>
                  </div>
                  <span className="px-3 py-1 bg-primary/10 text-primary-foreground rounded-full text-sm font-medium">
                    {playday.event_ids.length} events
                  </span>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Live Results Link */}
      <Link to="/contestant/live">
        <Card className="border-accent/30 bg-accent/5 hover:bg-accent/10 transition-colors">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 bg-accent/20 rounded-xl flex items-center justify-center">
              <Trophy className="h-6 w-6 text-accent" />
            </div>
            <div className="flex-1">
              <h3 className="font-heading font-bold text-text-primary">Live Results</h3>
              <p className="text-sm text-text-secondary">View real-time draws and results</p>
            </div>
            <span className="px-2 py-1 bg-accent text-white text-xs font-bold rounded animate-pulse">
              LIVE
            </span>
          </CardContent>
        </Card>
      </Link>
    </div>
  );
};

export default ContestantHomePage;
